/**
 * Maps Questionnaire Details payload onto the Matches tab "current test" row.
 * TestDetailsInfo sends nested companyTestType.name and location, not companyTestTypeName/site.
 */

export type CurrentMatchInstance = {
  createTime?: string;
  siteName?: string;
  companyJobName?: string;
  companyTestType?: { name?: string };
};

export type CurrentMatchTest = {
  id?: number;
  firstName?: string;
  lastName?: string;
  createTime?: string;
  welcomeTime?: string;
  submitTime?: string;
  companyTestType?: { name?: string };
  companyTestTypeName?: string;
  location?: string;
  site?: string;
  job?: string;
  testInstances?: CurrentMatchInstance[];
};

export type CurrentMatchRow = {
  testId?: number;
  firstName?: string;
  lastName?: string;
  createTime?: string;
  welcomeTime?: string;
  submitTime?: string;
  companyTestTypeName?: string;
  siteName?: string;
  jobName?: string;
};

function latestInstance(test: CurrentMatchTest): CurrentMatchInstance | undefined {
  const list = Array.isArray(test.testInstances) ? test.testInstances : [];
  if (list.length === 0) return undefined;
  return [...list].sort((a, b) => {
    const aMs = Date.parse(a.createTime || "") || 0;
    const bMs = Date.parse(b.createTime || "") || 0;
    return bMs - aMs;
  })[0];
}

export function buildCurrentMatchRow(test: CurrentMatchTest): CurrentMatchRow {
  const latest = latestInstance(test);
  return {
    testId: test.id,
    firstName: test.firstName,
    lastName: test.lastName,
    createTime: test.createTime,
    welcomeTime: test.welcomeTime,
    submitTime: test.submitTime,
    companyTestTypeName:
      test.companyTestType?.name ||
      latest?.companyTestType?.name ||
      test.companyTestTypeName,
    siteName: test.location || latest?.siteName || test.site,
    jobName: test.job || latest?.companyJobName,
  };
}

export type ActiveMatchRow = {
  testId?: number;
  eliminated?: boolean | null;
};

/** Active = not explicitly eliminated. Omitted/undefined counts (API drops null eliminated). */
export function countActiveMatches(matches: ActiveMatchRow[] | null | undefined): number {
  if (!Array.isArray(matches)) return 0;
  return matches.filter((m) => m?.eliminated !== true).length;
}

export type MatchDetailsSource = {
  testId?: number;
  testInstanceId?: number | string | null;
} | null | undefined;

/** Latest instance id from a match row; null if the API omitted it or it is 0. */
export function matchDetailsInstanceId(match: MatchDetailsSource): number | null {
  const id = Number(match?.testInstanceId ?? 0);
  return Number.isFinite(id) && id > 0 ? id : null;
}
